import { Box, SxProps, Theme } from '@mui/material';
import { ImageOutlined } from '@mui/icons-material';
import { ReactElement, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

export interface PaloThumbProps {
    src?: string;
    alt?: string;
    width: number | string;
    height: number;
    /** Applied to the picture and to the placeholder alike. */
    sx?: SxProps<Theme>;
}

/**
 * A picture in a list, with something to show when there is none.
 *
 * A file that has gone missing leaves the browser's broken-image mark behind,
 * which reads as a fault in the mask rather than as a missing file. The frame
 * stays the same size either way, so the rows do not jump.
 */
export const PaloThumb = ({ src, alt = '', width, height, sx }: PaloThumbProps): ReactElement => {
    const [failed, setFailed] = useState(false);

    // A row reused for another record has to try its own picture again.
    useEffect(() => setFailed(false), [src]);

    if (!src || failed) {
        return (
            <Box
                sx={{
                    width,
                    height,
                    borderRadius: '3px',
                    backgroundColor: c.neutralSoft,
                    border: `1px solid ${c.line}`,
                    display: 'grid',
                    placeItems: 'center',
                    color: c.ink3,
                    '& svg': { fontSize: Math.min(height - 6, 18) },
                    ...sx,
                }}
            >
                <ImageOutlined />
            </Box>
        );
    }

    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            onError={() => setFailed(true)}
            sx={{ width, height, objectFit: 'cover', display: 'block', borderRadius: '3px', ...sx }}
        />
    );
};
